import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FiUsers,
  FiPackage,
  FiClipboard,
  FiTool,
  FiTrendingUp,
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
} from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import siteVisitService from '../services/siteVisitService';
import inventoryService from '../services/inventoryService';
import './Dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsResponse, lowStockResponse] = await Promise.all([
        siteVisitService.getDashboardStats(),
        inventoryService.getLowStockAlerts(),
      ]);
      setStats(statsResponse.data);
      setLowStockItems(lowStockResponse.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const chartData = [
    { name: 'Mon', visits: 12 },
    { name: 'Tue', visits: 19 },
    { name: 'Wed', visits: 15 },
    { name: 'Thu', visits: 22 },
    { name: 'Fri', visits: 18 },
    { name: 'Sat', visits: 8 },
    { name: 'Sun', visits: 5 },
  ];

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h2 className="dashboard-title">Dashboard Overview</h2>
          <p className="dashboard-subtitle">Track your shop's performance at a glance</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card stat-card-primary">
          <div className="stat-icon">
            <FiClipboard />
          </div>
          <div className="stat-content">
            <p className="stat-label">Total Visits</p>
            <h3 className="stat-value">{stats?.total || 0}</h3>
            <p className="stat-change positive">
              <FiTrendingUp /> +12% from last month
            </p>
          </div>
        </div>

        <div className="stat-card stat-card-warning">
          <div className="stat-icon">
            <FiClock />
          </div>
          <div className="stat-content">
            <p className="stat-label">Scheduled</p>
            <h3 className="stat-value">{stats?.scheduled || 0}</h3>
            <p className="stat-description">Upcoming appointments</p>
          </div>
        </div>

        <div className="stat-card stat-card-info">
          <div className="stat-icon">
            <FiUsers />
          </div>
          <div className="stat-content">
            <p className="stat-label">In Progress</p>
            <h3 className="stat-value">{stats?.inProgress || 0}</h3>
            <p className="stat-description">Active service calls</p>
          </div>
        </div>

        <div className="stat-card stat-card-success">
          <div className="stat-icon">
            <FiCheckCircle />
          </div>
          <div className="stat-content">
            <p className="stat-label">Completed</p>
            <h3 className="stat-value">{stats?.completed || 0}</h3>
            <p className="stat-change positive">
              <FiTrendingUp /> Great work!
            </p>
          </div>
        </div>
      </div>

      {/* Chart and Alerts Row */}
      <div className="dashboard-grid">
        <div className="dashboard-card chart-card">
          <div className="card-header">
            <h3 className="card-title">Weekly Performance</h3>
            <p className="card-subtitle">Service calls this week</p>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip
                  contentStyle={{
                    background: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  }}
                />
                <Bar dataKey="visits" fill="url(#colorGradient)" radius={[8, 8, 0, 0]} />
                <defs>
                  <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1a9dff" stopOpacity={1} />
                    <stop offset="100%" stopColor="#0087f0" stopOpacity={0.8} />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="dashboard-card alerts-card">
          <div className="card-header">
            <h3 className="card-title">
              <FiAlertCircle /> Low Stock Alerts
            </h3>
            <span className="badge badge-error">{lowStockItems.length}</span>
          </div>
          <div className="alerts-list">
            {lowStockItems.length > 0 ? (
              lowStockItems.slice(0, 5).map((item) => (
                <div key={item._id} className="alert-item">
                  <div className="alert-icon">
                    <FiPackage />
                  </div>
                  <div className="alert-content">
                    <p className="alert-name">{item.itemName}</p>
                    <p className="alert-stock">
                      Stock: <span className="stock-value">{item.quantity}</span> / {item.minStockLevel}
                    </p>
                  </div>
                  <span className="badge badge-warning">Low</span>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <FiCheckCircle className="empty-icon" />
                <p>All items are well stocked!</p>
              </div>
            )}
          </div>
          {lowStockItems.length > 0 && (
            <Link to="/inventory" className="card-link">
              View All Inventory →
            </Link>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h3 className="section-title">Quick Actions</h3>
        <div className="actions-grid">
          <Link to="/site-visits/new" className="action-card">
            <div className="action-icon action-icon-primary">
              <FiClipboard />
            </div>
            <h4>Create Site Visit</h4>
            <p>Schedule a new service call</p>
          </Link>

          <Link to="/technicians/new" className="action-card">
            <div className="action-icon action-icon-success">
              <FiUsers />
            </div>
            <h4>Add Technician</h4>
            <p>Register new staff member</p>
          </Link>

          <Link to="/inventory/new" className="action-card">
            <div className="action-icon action-icon-warning">
              <FiPackage />
            </div>
            <h4>Add Inventory</h4>
            <p>Stock new items</p>
          </Link>

          <Link to="/tools/new" className="action-card">
            <div className="action-icon action-icon-info">
              <FiTool />
            </div>
            <h4>Register Tool</h4>
            <p>Add new equipment</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
